/**
 * Procedural walk and run cycles.
 *
 * A character's pose is a pure function of three numbers: how fast it is
 * moving over the ground, how far through its stride cycle it is, and the clock
 * (for breathing when it stands still). No animation clips, no mixer, no
 * skeleton asset — which is what lets a whole crowd be animated for the price of
 * a few trigonometric calls each, and what lets this file port to Unreal as-is.
 *
 * The one rule that matters: **the cycle is driven by distance, not by time.**
 * `advancePhase` moves the phase on by ground covered divided by stride length,
 * so a planted foot stays planted whatever speed the character is doing. Driving
 * the cycle from the clock is the classic mistake — feet skate across the
 * ground the moment speed and cadence disagree.
 *
 * Angles are radians. Limbs hang down from their joints and forward is -Z, so
 * a positive `thigh` or `shoulder` swings the limb forward, a positive `knee`
 * folds the shin back behind the thigh, and a positive `elbow` brings the
 * forearm forward and up.
 */

export interface LimbPair {
  left: number;
  right: number;
}

export interface GaitPose {
  /** Vertical offset of the hips from standing height, metres. */
  bob: number;
  /** Forward lean of the whole body from the hips, radians. */
  lean: number;
  /** Hips roll toward the stance leg. */
  sway: number;
  /** Torso yaw against the hips — the counter-rotation that swings the arms. */
  twist: number;
  thigh: LimbPair;
  knee: LimbPair;
  ankle: LimbPair;
  shoulder: LimbPair;
  elbow: LimbPair;
  /** Arms away from the body, radians. Positive is out to the side. */
  spread: number;
  /** Each arm out further on its own, radians: a hook's elbow up. Absent, neither. */
  armOut?: LimbPair;
  /** Head nod, radians. Positive looks down. */
  nod: number;
}

/** Body-plan the cycle is tuned for. Scale speeds and strides together. */
export const GAIT = {
  /** Metres per full stride cycle (two steps) while walking. */
  walkStride: 1.5,
  /** Metres per full stride cycle while running — a run is mostly flight. */
  runStride: 3.1,
  /** Speed at which the walk starts blending into a run, m/s. */
  runStart: 2.6,
  /** Speed at which the blend is a pure run, m/s. */
  runFull: 4.6,
  /** Below this a character is standing, not walking very slowly. */
  standStill: 0.08,
} as const;

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function mix(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** How much of a run the current speed is, 0 walking .. 1 running. */
export function runBlend(speed: number): number {
  return smoothstep(GAIT.runStart, GAIT.runFull, speed);
}

/** Stride length at a speed, blending walk into run. */
export function strideLength(speed: number): number {
  return mix(GAIT.walkStride, GAIT.runStride, runBlend(speed));
}

/**
 * Move the cycle on by the ground covered this frame. Returns a phase in [0,1).
 *
 * The cadence this implies is the check that the tuning is sane: walking at
 * 1.4 m/s it is 0.93 cycles a second, 112 steps a minute, and running at 6 m/s
 * it is 1.9 cycles a second — both within what people actually do.
 */
export function advancePhase(phase: number, speed: number, dt: number): number {
  if (speed <= GAIT.standStill) return phase;
  const next = phase + (speed * dt) / strideLength(speed);
  return next - Math.floor(next);
}

interface LegAngles {
  thigh: number;
  knee: number;
  ankle: number;
}

/**
 * One leg at a point in its own cycle.
 *
 * `p` is 0 when the leg passes under the body mid-stance, swinging back; the
 * leg reaches its furthest back at 0.25, swings through at 0.5 and plants in
 * front at 0.75. The knee folds during the forward swing — when the thigh is
 * moving forward, which is where `cos` is positive — and that fold is what
 * separates a walk from a pair of stilts.
 */
function leg(p: number, run: number, amount: number): LegAngles {
  const angle = p * Math.PI * 2;
  const s = -Math.sin(angle);
  const swing = Math.max(0, -Math.cos(angle));

  const thigh = amount * (mix(0.42, 0.78, run) * s + mix(0.02, 0.2, run));
  const knee = amount * (mix(0.1, 0.35, run) + mix(0.8, 1.75, run) * Math.pow(swing, mix(1.6, 1.1, run)));
  // Keep the sole roughly level: the foot's pitch in the world is
  // thigh - knee + ankle, so cancel most of the leg's net rotation.
  const ankle = -(thigh - knee) * mix(0.72, 0.45, run) - amount * 0.05;
  return { thigh, knee, ankle };
}

/**
 * The pose for a character moving at `speed` m/s, `phase` through its cycle.
 *
 * `time` only drives the idle breathing, so standing characters are not frozen
 * statues. A crowd should pass each character its own offset in `time`, or
 * everyone in the square breathes in unison.
 */
export function gaitPose(speed: number, phase: number, time: number): GaitPose {
  const run = runBlend(speed);
  // Swings grow in as a character starts off, rather than snapping to full
  // amplitude on the first frame of movement.
  const amount = smoothstep(GAIT.standStill, 0.9, speed);

  const left = leg(phase, run, amount);
  const right = leg((phase + 0.5) % 1, run, amount);

  const angle = phase * Math.PI * 2;
  const s = -Math.sin(angle);

  // The body is highest when the legs pass each other, twice a cycle. A run
  // inverts that: the high point is the flight phase, between footfalls.
  const walkBob = 0.035 * Math.cos(angle * 2);
  const runBob = 0.07 * Math.abs(Math.sin(angle)) - 0.045;
  const breathing = (1 - amount) * 0.006 * Math.sin(time * 1.9);

  const armSwing = mix(0.34, 0.68, run) * amount;
  const elbowBase = mix(0.22, 1.45, run);

  return {
    bob: amount * mix(walkBob - 0.01, runBob, run) + breathing,
    lean: mix(0.03, 0.2, run) * amount,
    sway: amount * 0.045 * s * (1 - run * 0.6),
    twist: amount * mix(0.1, 0.18, run) * s,
    thigh: { left: left.thigh, right: right.thigh },
    knee: { left: left.knee, right: right.knee },
    ankle: { left: left.ankle, right: right.ankle },
    // Arms swing against the leg on their own side.
    shoulder: { left: -armSwing * s, right: armSwing * s },
    elbow: {
      left: elbowBase + amount * mix(0.18, 0.25, run) * Math.max(0, -s),
      right: elbowBase + amount * mix(0.18, 0.25, run) * Math.max(0, s),
    },
    spread: mix(0.07, 0.12, run) + (1 - amount) * 0.02 * Math.sin(time * 1.9 + 0.8),
    nod: mix(0.02, 0.1, run) * amount,
  };
}

/**
 * Airborne poses: spread-eagle in freefall, hanging from the risers under a
 * canopy. Both take `time` so the body is never perfectly still.
 */
export function airPose(kind: 'freefall' | 'canopy', time: number, dive = 0): GaitPose {
  const flutter = Math.sin(time * 7.3) * 0.04;
  if (kind === 'freefall' && dive > 0) return mixPose(airPose('freefall', time), divePose(time), dive);
  if (kind === 'freefall') {
    // Arched belly-to-earth. The arms spread in the plane of the back, past
    // horizontal toward the head — the classic "W" — rather than swinging, which
    // would point them at the ground. Knees bent so the shins rise behind.
    return {
      bob: 0,
      lean: 0,
      sway: flutter * 0.5,
      twist: 0,
      thigh: { left: -0.12, right: -0.12 },
      knee: { left: 0.85 + flutter, right: 0.85 - flutter },
      ankle: { left: 0.3, right: 0.3 },
      shoulder: { left: 0.12, right: 0.12 },
      elbow: { left: 0.45, right: 0.45 },
      spread: 2.05 + flutter,
      nod: -0.7,
    };
  }
  // Under canopy: hands up on the toggles, legs hanging slightly forward and
  // together, swinging a little in the airflow.
  const swing = Math.sin(time * 1.4) * 0.08;
  return {
    bob: 0,
    lean: -0.05,
    sway: swing * 0.3,
    twist: 0,
    thigh: { left: 0.25 + swing, right: 0.3 + swing },
    knee: { left: 0.35, right: 0.3 },
    ankle: { left: -0.3, right: -0.3 },
    shoulder: { left: 2.75, right: 2.75 },
    elbow: { left: 0.35, right: 0.35 },
    spread: 0.28,
    nod: -0.15,
  };
}

/**
 * Head-down in a dive: arms swept back along the sides, legs together and
 * straight, the head up to watch where it is going. The body's own tip
 * toward the ground is the renderer's (`bodyPitch`); this is the limbs.
 */
function divePose(time: number): GaitPose {
  const flutter = Math.sin(time * 11) * 0.03;
  return {
    bob: 0,
    lean: 0,
    sway: flutter * 0.4,
    twist: 0,
    thigh: { left: 0, right: 0 },
    knee: { left: 0.12 + flutter, right: 0.12 - flutter },
    ankle: { left: 0.45, right: 0.45 },
    shoulder: { left: -0.25, right: -0.25 },
    elbow: { left: 0.1, right: 0.1 },
    spread: 0.35 + flutter,
    nod: -1.0,
  };
}

/** Part way from pose `a` to pose `b`, `t` 0..1. */
export function mixPose(a: GaitPose, b: GaitPose, t: number): GaitPose {
  const m = (x: number, y: number) => x + (y - x) * t;
  const pair = (x: LimbPair, y: LimbPair): LimbPair => ({ left: m(x.left, y.left), right: m(x.right, y.right) });
  return {
    bob: m(a.bob, b.bob), lean: m(a.lean, b.lean), sway: m(a.sway, b.sway), twist: m(a.twist, b.twist),
    thigh: pair(a.thigh, b.thigh), knee: pair(a.knee, b.knee), ankle: pair(a.ankle, b.ankle),
    shoulder: pair(a.shoulder, b.shoulder), elbow: pair(a.elbow, b.elbow),
    spread: m(a.spread, b.spread), nod: m(a.nod, b.nod),
  };
}

/** The jump's take-off speed, m/s, as `PlayerController` jumps: what `jumpPose` measures the climb against. */
export const JUMP_UP = 4.5;

/**
 * In the air after a jump, by how fast it is climbing (`vy`, m/s, negative
 * falling) and how fast it is moving over the ground (`speed`, m/s).
 *
 * Standing, a jump is a spring: legs driving straight off the ground, toes
 * pointed and the arms swung up to lift; the knees tucked under at the top;
 * the legs reaching down for the ground as it comes, the arms out to
 * balance. Running, it is a leap: the `lead` leg (1 the left, -1 the right)
 * thrown forward and bent, the other trailing folded behind, the opposite
 * arm forward and the body leaning into it; coming down, the lead leg
 * reaches out to land on. Between the two, a blend by speed.
 */
export function jumpPose(vy: number, time: number, speed = 0, lead: 1 | -1 = 1): GaitPose {
  // 1 just off the ground .. 0 at the top .. negative coming down.
  const u = Math.max(-1.5, Math.min(1, vy / JUMP_UP));
  const push = smooth01((u - 0.55) / 0.45);
  const tuck = Math.max(0, 1 - Math.abs(u - 0.15) / 0.75) * (1 - push);
  const reach = smooth01(-u / 1.2);
  const flutter = Math.sin(time * 9) * 0.015;
  // Standing: a spring up and down.
  const springThigh = 0.15 * push + 0.85 * tuck + 0.35 * reach;
  const springKnee = 0.1 * push + 1.4 * tuck + 0.25 * reach;
  const spring: GaitPose = {
    bob: 0.04 * push,
    lean: 0.08 * push + 0.05 * tuck - 0.03 * reach,
    sway: flutter,
    twist: 0,
    thigh: { left: springThigh, right: springThigh * 0.92 },
    knee: { left: springKnee, right: springKnee * 0.95 },
    // Toes pointed off the ground, flexed up to meet it.
    ankle: { left: -0.45 * push + 0.2 * tuck + 0.1 * reach, right: -0.45 * push + 0.2 * tuck + 0.1 * reach },
    shoulder: { left: 2.3 * push + 1.4 * tuck + 0.5 * reach + 0.2, right: 2.3 * push + 1.4 * tuck + 0.5 * reach + 0.2 },
    elbow: { left: 0.3 + 0.3 * tuck, right: 0.3 + 0.3 * tuck },
    spread: 0.15 + 0.7 * reach,
    nod: 0.1 * tuck - 0.12 * reach,
  };
  // Running: a leap, one leg ahead and one behind.
  const fore = 0.9 + 0.25 * tuck - 0.1 * reach, aft = -0.65 + 0.15 * reach;
  const foreKnee = 0.7 + 0.4 * tuck - 0.5 * reach, aftKnee = 1.5 - 0.5 * reach;
  const legL = lead === 1, arm = 0.9;
  const leap: GaitPose = {
    bob: 0.03,
    lean: 0.22 - 0.08 * reach,
    sway: flutter,
    twist: 0.18 * lead,
    thigh: { left: legL ? fore : aft, right: legL ? aft : fore },
    knee: { left: legL ? foreKnee : aftKnee, right: legL ? aftKnee : foreKnee },
    ankle: { left: legL ? 0.15 : -0.35, right: legL ? -0.35 : 0.15 },
    // The arm opposite the lead leg forward, as in a stride.
    shoulder: { left: legL ? -0.6 : arm, right: legL ? arm : -0.6 },
    elbow: { left: legL ? 0.5 : 1.1, right: legL ? 1.1 : 0.5 },
    spread: 0.12 + 0.25 * reach,
    nod: -0.05,
  };
  return blendPose(spring, leap, smooth01((speed - 1.2) / 3));
}

/**
 * Just landed, `k` 1 at touchdown easing to 0: the legs giving under the
 * weight and straightening again, laid over `base` (the walk it lands into).
 */
export function landPose(base: GaitPose, k: number): GaitPose {
  if (k <= 0) return base;
  return {
    ...base,
    bob: base.bob - 0.16 * k,
    lean: base.lean + 0.18 * k,
    thigh: { left: base.thigh.left + 0.55 * k, right: base.thigh.right + 0.5 * k },
    knee: { left: base.knee.left + 1.0 * k, right: base.knee.right + 0.95 * k },
    ankle: { left: base.ankle.left + 0.35 * k, right: base.ankle.right + 0.35 * k },
    shoulder: { left: base.shoulder.left + 0.35 * k, right: base.shoulder.right + 0.35 * k },
    spread: base.spread + 0.2 * k,
  };
}

function smooth01(x: number): number {
  const t = Math.max(0, Math.min(1, x));
  return t * t * (3 - 2 * t);
}

function blendPose(a: GaitPose, b: GaitPose, t: number): GaitPose {
  const m = (x: number, y: number) => x + (y - x) * t;
  const pair = (x: LimbPair, y: LimbPair) => ({ left: m(x.left, y.left), right: m(x.right, y.right) });
  return {
    bob: m(a.bob, b.bob), lean: m(a.lean, b.lean), sway: m(a.sway, b.sway), twist: m(a.twist, b.twist),
    thigh: pair(a.thigh, b.thigh), knee: pair(a.knee, b.knee), ankle: pair(a.ankle, b.ankle),
    shoulder: pair(a.shoulder, b.shoulder), elbow: pair(a.elbow, b.elbow), spread: m(a.spread, b.spread), nod: m(a.nod, b.nod),
  };
}

